import { useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Indicator,
  Paper,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { formatDate } from '@/lib/helpers';
import {
  useLegalRequests,
  type LegalRequestType,
  type TLegalRequest,
} from '@/models/offers';
import Loader from '@/components/Loader';
import LegalRequestCreateModal from './LegalRequestCreateModal';
import LegalRequestDrawer from './LegalRequestDrawer';

const REQUEST_TYPE_LABELS: Record<LegalRequestType, string> = {
  legal_advice: 'Legal Advice',
  nda: 'NDA',
  evaluation_lo: 'Eval LO',
  contract_review: 'Contract',
};

const STATUS_COLORS: Record<string, string> = {
  New: 'gray',
  'In Progress': 'blue',
  'Working on it': 'blue',
  Done: 'green',
  Approved: 'green',
  Rejected: 'red',
  Stuck: 'orange',
  Denied: 'red',
};

const TYPE_COLORS: Record<LegalRequestType, string> = {
  legal_advice: 'blue',
  nda: 'violet',
  evaluation_lo: 'orange',
  contract_review: 'teal',
};

export default function LegalRequestPanel({
  offerId,
  offerAccepted,
  invoiceStatus,
}: {
  offerId: number;
  offerAccepted: boolean;
  invoiceStatus: string | null;
}) {
  const { data: requests = [], isLoading } = useLegalRequests(offerId);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [activeRequestType, setActiveRequestType] =
    useState<LegalRequestType | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<TLegalRequest | null>(
    null,
  );

  const handleCreate = (type: LegalRequestType) => {
    setActiveRequestType(type);
    openModal();
  };

  const isPaid = invoiceStatus === 'paid';

  return (
    <>
      <Paper withBorder p="sm">
        <Group justify="space-between" mb="xs">
          <Text fw={500} fz="sm">
            Legal Requests
          </Text>
        </Group>

        <Button.Group mb="sm">
          <Button
            size="compact-xs"
            variant="light"
            disabled={isPaid}
            onClick={() => handleCreate('legal_advice')}
          >
            Legal Advice
          </Button>
          <Button
            size="compact-xs"
            variant="light"
            disabled={isPaid}
            onClick={() => handleCreate('nda')}
          >
            NDA
          </Button>
          <Button
            size="compact-xs"
            variant="light"
            disabled={offerAccepted}
            onClick={() => handleCreate('evaluation_lo')}
          >
            Eval LO
          </Button>
          <Button
            size="compact-xs"
            variant="light"
            disabled={isPaid}
            onClick={() => handleCreate('contract_review')}
          >
            Contract
          </Button>
        </Button.Group>

        {isLoading && <Loader />}

        {!isLoading && requests.length === 0 && (
          <Text fz="xs" c="dimmed">
            No legal requests yet.
          </Text>
        )}

        {requests.length > 0 && (
          <Table highlightOnHover fz="xs" verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ whiteSpace: 'nowrap' }}>
                  Last update
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {requests.map((req) => (
                <Table.Tr
                  key={req.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedRequest(req)}
                >
                  <Table.Td>
                    <Indicator
                      color="blue"
                      size={6}
                      offset={-2}
                      disabled={!req.hasUnread}
                      inline
                    >
                      <Badge
                        size="xs"
                        color={TYPE_COLORS[req.requestType]}
                        variant="light"
                      >
                        {REQUEST_TYPE_LABELS[req.requestType]}
                      </Badge>
                    </Indicator>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={STATUS_COLORS[req.mondayStatus] ?? 'gray'}
                      variant="light"
                    >
                      {req.mondayStatus}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text fz="xs" c="dimmed">
                      {formatDate(req.updatedAt)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <LegalRequestCreateModal
        opened={modalOpened}
        onClose={closeModal}
        requestType={activeRequestType}
        offerId={offerId}
      />

      <LegalRequestDrawer
        opened={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        legalRequest={selectedRequest}
      />
    </>
  );
}
